import { parseCoordinateValue } from '@gmn-portal/backend';
import { Copy, MapPin, Plus, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { toast } from 'sonner';
import { useIntl } from '@/core/i18n/use-intl';
import { useCoordinateFormat } from '@/shared/hooks/use-coordinate-format';
import type { Position } from '@/shared/schema';
import {
  cn,
  formatCoordinateForCopy,
  getCoordinatePlaceholder,
  toEditablePosition,
  tryParseCoordinatePair,
} from '@/shared/utils';
import { Button } from '../../button';
import { Card, CardContent } from '../../card';
import { Checkbox } from '../../checkbox';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

type EditablePosition = {
  id: string;
  label: string;
  latitude: string;
  longitude: string;
  status?: boolean;
};

function ensurePositionsArray(
  value: unknown,
  format: ReturnType<typeof useCoordinateFormat>
): EditablePosition[] {
  if (!value || !Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is Position =>
        item && typeof item === 'object' && typeof (item as Position).id === 'string'
    )
    .map((pos) => {
      const editable = toEditablePosition(pos, format);
      return {
        id: editable.id,
        label: editable.label ?? '',
        latitude: editable.latitude,
        longitude: editable.longitude,
        status: editable.status ?? false,
      };
    });
}

function createEmptyPosition(showStatus: boolean): EditablePosition {
  return {
    id: nanoid(8),
    label: '',
    latitude: '',
    longitude: '',
    ...(showStatus ? { status: false } : {}),
  };
}

interface PositionInputProps {
  id: string;
  testId: string;
  initialValue: string;
  onValueChange: (value: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  label: string;
  labelClassName?: string;
  inputClassName?: string;
}

const PositionInput = memo(function PositionInput({
  id,
  testId,
  initialValue,
  onValueChange,
  onPaste,
  placeholder,
  disabled,
  label,
  labelClassName,
  inputClassName,
}: PositionInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastReportedValue = useRef(initialValue);

  useEffect(() => {
    if (inputRef.current && inputRef.current.value === '') {
      inputRef.current.value = initialValue;
    }
  }, []);

  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      if (inputRef.current.value !== initialValue) {
        inputRef.current.value = initialValue;
        lastReportedValue.current = initialValue;
      }
    }
  }, [initialValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue !== lastReportedValue.current) {
        lastReportedValue.current = newValue;
        onValueChange(newValue);
      }
    },
    [onValueChange]
  );

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={cn('text-xs', labelClassName)}>
        {label}
      </Label>
      <Input
        ref={inputRef}
        id={id}
        data-testid={testId}
        defaultValue={initialValue}
        onChange={handleChange}
        onPaste={onPaste}
        placeholder={placeholder}
        disabled={disabled}
        className={cn('h-9 md:h-8', inputClassName)}
      />
    </div>
  );
});

const PositionStatus = memo(function PositionStatus({
  id,
  testId,
  initialChecked,
  onCheckedChange,
  disabled,
  statusLabel,
  labelClassName,
}: {
  id: string;
  testId: string;
  initialChecked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  statusLabel: string;
  labelClassName?: string;
}) {
  const handleChange = useCallback(
    (checked: boolean | 'indeterminate') => {
      onCheckedChange(checked === true);
    },
    [onCheckedChange]
  );

  return (
    <div className="flex items-center gap-2 pt-1 md:pt-0 md:justify-center">
      <Checkbox
        id={id}
        data-testid={testId}
        defaultChecked={initialChecked}
        onCheckedChange={handleChange}
        disabled={disabled}
      />
      <Label htmlFor={id} className={cn('text-sm cursor-pointer', labelClassName)}>
        {statusLabel}
      </Label>
    </div>
  );
});

const toStoredPositions = (
  positions: EditablePosition[],
  showStatus: boolean
): Array<Record<string, unknown>> | undefined => {
  const cleaned = positions.map((pos) => {
    const latitude = parseCoordinateValue(pos.latitude, 'latitude');
    const longitude = parseCoordinateValue(pos.longitude, 'longitude');
    const latitudeText = pos.latitude.trim();
    const longitudeText = pos.longitude.trim();

    return {
      id: pos.id,
      label: pos.label ?? '',
      ...(latitude !== null ? { latitude } : latitudeText ? { latitude: pos.latitude } : {}),
      ...(longitude !== null ? { longitude } : longitudeText ? { longitude: pos.longitude } : {}),
      ...(showStatus ? { status: pos.status ?? false } : {}),
    };
  });

  return cleaned.length > 0 ? cleaned : undefined;
};

const copyCoordinates = async (text: string, successMessage: string, errorMessage: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error(errorMessage);
  }
};

const PositionRow = memo(function PositionRow({
  position,
  index,
  onUpdate,
  onRemove,
  onCoordinatePaste,
  disabled,
  showStatus,
  statusLabel,
  coordinateFormat,
}: {
  position: EditablePosition;
  index: number;
  onUpdate: (id: string, updates: Partial<EditablePosition>) => void;
  onRemove: (id: string) => void;
  onCoordinatePaste: (id: string, lat: string, lng: string) => void;
  disabled?: boolean;
  showStatus?: boolean;
  statusLabel?: string;
  coordinateFormat: ReturnType<typeof useCoordinateFormat>;
}) {
  const { t } = useIntl();
  const positionId = position.id;
  const gridCols = showStatus
    ? 'md:grid-cols-[1.4fr_1fr_1fr_minmax(10rem,0.8fr)_auto]'
    : 'md:grid-cols-[1.4fr_1fr_1fr_auto]';

  const handleLatPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const parsed = tryParseCoordinatePair(pastedText);
      if (parsed) {
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        target.value = parsed[0];
        onCoordinatePaste(positionId, parsed[0], parsed[1]);
      }
    },
    [positionId, onCoordinatePaste]
  );

  const handleLongPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const parsed = tryParseCoordinatePair(pastedText);
      if (parsed) {
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        target.value = parsed[1];
        onCoordinatePaste(positionId, parsed[0], parsed[1]);
      }
    },
    [positionId, onCoordinatePaste]
  );

  const canCopy =
    parseCoordinateValue(position.latitude, 'latitude') !== null &&
    parseCoordinateValue(position.longitude, 'longitude') !== null;

  const handleCopy = useCallback(() => {
    const latitude = parseCoordinateValue(position.latitude, 'latitude');
    const longitude = parseCoordinateValue(position.longitude, 'longitude');
    if (latitude === null || longitude === null) return;
    void copyCoordinates(
      formatCoordinateForCopy({ latitude, longitude }, coordinateFormat),
      t('metadata.positions.copySuccess'),
      t('metadata.positions.copyError')
    );
  }, [coordinateFormat, position.latitude, position.longitude, t]);

  return (
    <div
      className="grid gap-2 p-2 rounded-lg border bg-muted/20"
      data-testid={`position-row-${index}`}
    >
      <div className={cn('grid gap-2 md:items-center', gridCols)}>
        <PositionInput
          id={`pos-${positionId}-label`}
          testId={`position-${index}-label`}
          initialValue={position.label ?? ''}
          onValueChange={(value) => onUpdate(positionId, { label: value })}
          placeholder={t('metadata.positions.labelPlaceholder')}
          disabled={disabled}
          label={t('metadata.positions.label')}
          labelClassName="md:hidden"
        />

        <PositionInput
          id={`pos-${positionId}-lat`}
          testId={`position-${index}-latitude`}
          initialValue={position.latitude ?? ''}
          onValueChange={(value) => onUpdate(positionId, { latitude: value })}
          onPaste={handleLatPaste}
          placeholder={getCoordinatePlaceholder('latitude', coordinateFormat)}
          disabled={disabled}
          label={t('metadata.positions.latitude')}
          labelClassName="md:hidden"
        />

        <PositionInput
          id={`pos-${positionId}-long`}
          testId={`position-${index}-longitude`}
          initialValue={position.longitude ?? ''}
          onValueChange={(value) => onUpdate(positionId, { longitude: value })}
          onPaste={handleLongPaste}
          placeholder={getCoordinatePlaceholder('longitude', coordinateFormat)}
          disabled={disabled}
          label={t('metadata.positions.longitude')}
          labelClassName="md:hidden"
        />

        {showStatus && statusLabel && (
          <div className="flex items-center justify-center md:h-8">
            <PositionStatus
              id={`pos-${positionId}-status`}
              testId={`position-${index}-status`}
              initialChecked={position.status ?? false}
              onCheckedChange={(checked) => onUpdate(positionId, { status: checked })}
              disabled={disabled}
              statusLabel={statusLabel}
              labelClassName="md:hidden"
            />
          </div>
        )}

        <div className="flex items-end gap-1 md:items-center justify-end pt-2 md:pt-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            disabled={disabled || !canCopy}
            data-testid={`position-${index}-copy`}
            className="h-9 w-9 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-foreground"
            title={t('metadata.positions.copy')}
          >
            <Copy className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(positionId)}
            disabled={disabled}
            data-testid={`position-${index}-remove`}
            className="h-9 w-9 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-destructive"
            title={t('common.delete')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

export default function AutoFormPositions({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t, locale } = useIntl();
  const coordinateFormat = useCoordinateFormat();
  // Strip the react-hook-form field handlers before spreading onto the wrapper
  // element: `fieldProps` includes `...field`, and letting `onChange` land on the
  // <div> makes bubbling input events call `field.onChange(event)`, which clobbers
  // the positions array with a raw coordinate string.
  const {
    showLabel: _showLabel,
    disabled,
    onChange: _onChange,
    onBlur: _onBlur,
    value: _value,
    name: _name,
    ref: _ref,
    ...restFieldProps
  } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;

  const statusLabel =
    locale === 'de-DE'
      ? (fieldConfigItem?.statusLabel as string | undefined)
      : (fieldConfigItem?.statusLabelEn as string | undefined) ||
        (fieldConfigItem?.statusLabel as string | undefined);
  const showStatus = !!statusLabel;
  const headerGridCols = showStatus
    ? 'md:grid-cols-[1.4fr_1fr_1fr_minmax(10rem,0.8fr)_auto]'
    : 'md:grid-cols-[1.4fr_1fr_1fr_auto]';

  const [positions, setPositions] = useState<EditablePosition[]>(() =>
    ensurePositionsArray(field.value, coordinateFormat)
  );
  const isOwnUpdateRef = useRef(false);

  const fieldValueIds = useMemo(() => {
    return ensurePositionsArray(field.value, coordinateFormat)
      .map((p) => p.id)
      .join(',');
  }, [coordinateFormat, field.value]);

  const localIds = useMemo(() => positions.map((p) => p.id).join(','), [positions]);

  useEffect(() => {
    if (isOwnUpdateRef.current) {
      isOwnUpdateRef.current = false;
      return;
    }

    const nextPositions = ensurePositionsArray(field.value, coordinateFormat);
    if (fieldValueIds !== localIds || positions.length !== nextPositions.length) {
      setPositions(nextPositions);
    }
  }, [coordinateFormat, field.value, fieldValueIds, localIds, positions.length]);

  const syncToForm = useCallback(
    (nextPositions: EditablePosition[]) => {
      isOwnUpdateRef.current = true;
      field.onChange(toStoredPositions(nextPositions, showStatus));
    },
    [field, showStatus]
  );

  const updatePositions = useCallback(
    (updater: (previous: EditablePosition[]) => EditablePosition[]) => {
      setPositions((previous) => {
        const next = updater(previous);
        syncToForm(next);
        return next;
      });
    },
    [syncToForm]
  );

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60" data-testid="positions-field">
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            {showLabel && (
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                  <MapPin className="size-4 text-primary" />
                </div>
                <AutoFormLabel
                  label={fieldConfigItem?.label || label}
                  isRequired={isRequired}
                  className="text-sm font-semibold"
                />
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => updatePositions((prev) => [...prev, createEmptyPosition(showStatus)])}
              disabled={disabled}
              data-testid="add-position-button"
              className="h-8"
            >
              <Plus className="size-3.5 mr-1.5" />
              {t('metadata.positions.addPosition')}
            </Button>
          </div>
          {positions.length === 0 ? (
            <p
              className="text-sm text-muted-foreground py-4 text-center"
              data-testid="no-positions-message"
            >
              {t('metadata.positions.noPositions')}
            </p>
          ) : (
            <div className="space-y-3">
              <div className={cn('hidden md:grid gap-2 px-2', headerGridCols)}>
                <span className="text-xs font-medium text-muted-foreground">
                  {t('metadata.positions.label')}
                </span>
                <span className="text-xs font-medium text-muted-foreground pl-1">
                  {t('metadata.positions.latitude')}
                </span>
                <span className="text-xs font-medium text-muted-foreground pl-2">
                  {t('metadata.positions.longitude')}
                </span>
                {showStatus && (
                  <span className="text-xs font-medium text-muted-foreground px-3">
                    {statusLabel}
                  </span>
                )}
                <div className="w-[72px]" />
              </div>
              {positions.map((position, index) => (
                <PositionRow
                  key={position.id}
                  position={position}
                  index={index}
                  onUpdate={(id, updates) =>
                    updatePositions((prev) =>
                      prev.map((pos) => (pos.id === id ? { ...pos, ...updates } : pos))
                    )
                  }
                  onRemove={(id) => updatePositions((prev) => prev.filter((p) => p.id !== id))}
                  onCoordinatePaste={(id, lat, lng) =>
                    updatePositions((prev) =>
                      prev.map((pos) =>
                        pos.id === id ? { ...pos, latitude: lat, longitude: lng } : pos
                      )
                    )
                  }
                  disabled={disabled}
                  showStatus={showStatus}
                  statusLabel={statusLabel}
                  coordinateFormat={coordinateFormat}
                />
              ))}
            </div>
          )}

          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
